import { useEffect, useMemo, useState } from 'react';
import {
    ChartLineUp,
    Barbell,
    MagnifyingGlass,
} from '@phosphor-icons/react';
import { api, formatApiErrorDetail } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/components/ThemeProvider';
import { CalendarHeatmap } from '@/components/CalendarHeatmap';
import { ExerciseProgressionChart } from '@/components/ExerciseProgressionChart';
import { MuscleVolumeChart } from '@/components/MuscleVolumeChart';
import { INSIGHTS } from '@/constants/testIds';

const RANGE_OPTIONS = [
    { value: 30, label: '30d' },
    { value: 90, label: '90d' },
    { value: 180, label: '6m' },
    { value: 365, label: '1y' },
];

export default function Insights() {
    const { user } = useAuth();
    const { theme } = useTheme();
    const units = user?.units || 'kg';
    const isDark = theme === 'dark';

    const [exercises, setExercises] = useState([]);
    const [selectedExId, setSelectedExId] = useState('');
    const [exSearch, setExSearch] = useState('');
    const [progression, setProgression] = useState([]);
    const [muscleWeeks, setMuscleWeeks] = useState([]);
    const [heatmap, setHeatmap] = useState([]);
    const [days, setDays] = useState(180);
    const [visible, setVisible] = useState({
        weight: true,
        e1rm: true,
        volume: false,
    });
    const [error, setError] = useState('');
    const [loadingProg, setLoadingProg] = useState(false);

    // Bootstrap: catalog + muscle weeks + heatmap
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [exRes, mRes, hRes] = await Promise.all([
                    api.get('/exercises'),
                    api.get('/insights/muscle-volume', {
                        params: { weeks: 8 },
                    }),
                    api.get('/insights/heatmap', { params: { weeks: 26 } }),
                ]);
                if (cancelled) return;
                setExercises(exRes.data);
                setMuscleWeeks(mRes.data);
                setHeatmap(hRes.data);

                // Pick a default exercise: most-recently-used in progression
                // history. Cheapest signal: the first exercise that returns a
                // non-empty progression. Fallback: first in catalog.
                if (exRes.data.length) {
                    setSelectedExId((prev) => prev || exRes.data[0].id);
                }
            } catch (e) {
                if (!cancelled) {
                    setError(
                        formatApiErrorDetail(e.response?.data?.detail) ||
                            e.message
                    );
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // Fetch progression whenever selection / range changes
    useEffect(() => {
        if (!selectedExId) return;
        let cancelled = false;
        setLoadingProg(true);
        (async () => {
            try {
                const { data } = await api.get(
                    `/insights/exercise-progression/${selectedExId}`,
                    { params: { days } }
                );
                if (!cancelled) setProgression(data);
            } catch (e) {
                if (!cancelled) {
                    setError(
                        formatApiErrorDetail(e.response?.data?.detail) ||
                            e.message
                    );
                    setProgression([]);
                }
            } finally {
                if (!cancelled) setLoadingProg(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [selectedExId, days]);

    const filteredExercises = useMemo(() => {
        const q = exSearch.trim().toLowerCase();
        const arr = q
            ? exercises.filter((e) => e.name.toLowerCase().includes(q))
            : exercises;
        return arr.slice(0, 60);
    }, [exercises, exSearch]);

    const selectedExercise =
        exercises.find((e) => e.id === selectedExId) || null;

    return (
        <main
            data-testid={INSIGHTS.root}
            className="mx-auto max-w-5xl px-5 sm:px-8 pt-10 sm:pt-14 pb-32"
        >
            <div className="animate-fade-up">
                <span className="uppercase-mono text-muted-foreground">
                    Analytics
                </span>
                <h1 className="mt-2 font-display font-extrabold tracking-tighter text-4xl sm:text-5xl">
                    Insights
                </h1>
                <p className="mt-3 text-muted-foreground max-w-xl">
                    Three lenses on your training: per-exercise progression,
                    weekly muscle-group volume, and a 26-week training
                    frequency map.
                </p>
            </div>

            {error && (
                <p className="mt-5 text-sm text-[#FF4D4D] font-medium">
                    {error}
                </p>
            )}

            {/* Per-exercise progression */}
            <section
                data-testid={INSIGHTS.progressionSection}
                className="mt-10 glass-card rounded-3xl p-6 sm:p-8"
            >
                <div className="flex items-start gap-3 mb-5">
                    <span className="grid place-items-center w-10 h-10 rounded-2xl bg-foreground/5 border border-foreground/10 shrink-0">
                        <ChartLineUp size={18} weight="bold" />
                    </span>
                    <div className="min-w-0 flex-1">
                        <p className="uppercase-mono text-muted-foreground">
                            01 · Progression
                        </p>
                        <h2 className="font-display font-bold tracking-tight text-2xl">
                            {selectedExercise?.name || 'Pick an exercise'}
                        </h2>
                    </div>
                </div>

                {/* Exercise picker */}
                <div className="grid sm:grid-cols-[1fr_auto] gap-3 mb-5">
                    <div className="flex items-center gap-2 h-12 overflow-hidden rounded-2xl border border-foreground/15 bg-foreground/5 px-4">
                        <MagnifyingGlass
                            size={16}
                            weight="bold"
                            className="text-muted-foreground shrink-0"
                        />
                        <input
                            type="text"
                            value={exSearch}
                            onChange={(e) => setExSearch(e.target.value)}
                            placeholder="Filter exercises…"
                            className="hidden sm:block flex-1 min-w-0 h-full bg-transparent outline-none placeholder:text-muted-foreground/70"
                        />
                        <select
                            data-testid={INSIGHTS.exercisePicker}
                            value={selectedExId}
                            onChange={(e) => setSelectedExId(e.target.value)}
                            aria-label="Pick exercise"
                            className="flex-1 sm:flex-initial h-10 -my-1 min-w-0 max-w-full sm:max-w-[200px] bg-transparent outline-none font-mono text-sm font-semibold text-foreground cursor-pointer"
                        >
                            {filteredExercises.map((e) => (
                                <option key={e.id} value={e.id}>
                                    {e.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-center gap-1.5 p-1 rounded-2xl border border-foreground/15 bg-foreground/5">
                        {RANGE_OPTIONS.map((r) => (
                            <button
                                key={r.value}
                                type="button"
                                onClick={() => setDays(r.value)}
                                data-testid={`${INSIGHTS.rangeToggle}-${r.value}`}
                                className={`uppercase-mono flex-1 sm:flex-initial min-h-10 px-3 rounded-xl transition-colors ${
                                    days === r.value
                                        ? 'bg-foreground text-background'
                                        : 'text-muted-foreground hover:text-foreground'
                                }`}
                            >
                                {r.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Series toggles */}
                <div className="flex flex-wrap gap-2 mb-4">
                    <SeriesToggle
                        testId={INSIGHTS.seriesToggleWeight}
                        active={visible.weight}
                        color="#00FF9D"
                        label="Top set"
                        onClick={() =>
                            setVisible((v) => ({ ...v, weight: !v.weight }))
                        }
                    />
                    <SeriesToggle
                        testId={INSIGHTS.seriesToggleE1rm}
                        active={visible.e1rm}
                        color="#A78BFA"
                        label="e1RM"
                        onClick={() =>
                            setVisible((v) => ({ ...v, e1rm: !v.e1rm }))
                        }
                    />
                    <SeriesToggle
                        testId={INSIGHTS.seriesToggleVolume}
                        active={visible.volume}
                        color="#FF4D4D"
                        label="Volume"
                        onClick={() =>
                            setVisible((v) => ({ ...v, volume: !v.volume }))
                        }
                    />
                </div>

                {loadingProg ? (
                    <p className="uppercase-mono text-muted-foreground animate-soft-pulse">
                        Loading…
                    </p>
                ) : (
                    <ExerciseProgressionChart
                        points={progression}
                        units={units}
                        visible={visible}
                        isDark={isDark}
                    />
                )}
            </section>

            {/* Muscle volume */}
            <section
                data-testid={INSIGHTS.muscleVolumeSection}
                className="mt-8 glass-card rounded-3xl p-6 sm:p-8"
            >
                <div className="flex items-start gap-3 mb-5">
                    <span className="grid place-items-center w-10 h-10 rounded-2xl bg-foreground/5 border border-foreground/10 shrink-0">
                        <Barbell size={18} weight="bold" />
                    </span>
                    <div className="min-w-0 flex-1">
                        <p className="uppercase-mono text-muted-foreground">
                            02 · Weekly muscle volume
                        </p>
                        <h2 className="font-display font-bold tracking-tight text-2xl">
                            This week vs last
                        </h2>
                        <p className="text-sm text-muted-foreground mt-1">
                            Solid bars = current week. Translucent baseline =
                            previous week, for comparison.
                        </p>
                    </div>
                </div>
                <MuscleVolumeChart
                    weeks={muscleWeeks}
                    units={units}
                    isDark={isDark}
                />
            </section>

            {/* Heatmap */}
            <section className="mt-8 glass-card rounded-3xl p-6 sm:p-8">
                <div className="flex items-start gap-3 mb-5">
                    <span className="grid place-items-center w-10 h-10 rounded-2xl bg-foreground/5 border border-foreground/10 shrink-0">
                        <ChartLineUp size={18} weight="bold" />
                    </span>
                    <div>
                        <p className="uppercase-mono text-muted-foreground">
                            03 · Training frequency
                        </p>
                        <h2 className="font-display font-bold tracking-tight text-2xl">
                            Heatmap
                        </h2>
                    </div>
                </div>
                <div className="overflow-x-auto pb-2">
                    <CalendarHeatmap days={heatmap} weeks={26} units={units} />
                </div>
            </section>
        </main>
    );
}

function SeriesToggle({ testId, active, color, label, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={`uppercase-mono inline-flex items-center gap-1.5 rounded-full min-h-10 px-4 py-2 border transition-all ${
                active
                    ? 'border-foreground/30 bg-foreground/5'
                    : 'border-foreground/10 bg-transparent text-muted-foreground hover:bg-foreground/5'
            }`}
            style={active ? { color } : undefined}
        >
            <span
                className="w-2 h-2 rounded-full"
                style={{
                    background: color,
                    opacity: active ? 1 : 0.4,
                }}
            />
            {label}
        </button>
    );
}
